import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { UserProfile } from '@bcc/shared';
import { ROLES } from '@bcc/shared';
import { useAuth } from '../contexts/AuthContext';
import { SITE_GLASS_CARD } from '../constants/siteUi';
import { profileDisplayName, profileInitials } from '../lib/profileDisplay';

function AvatarThumb({ profile }: { profile: UserProfile }) {
  const [broken, setBroken] = useState(false);
  const url = profile.avatar_url;
  const initials = profileInitials(profile);

  const ring =
    'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/[0.08]';

  if (url && !broken) {
    return (
      <div className={ring} aria-hidden>
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setBroken(true)}
        />
      </div>
    );
  }

  return (
    <span className={`${ring} text-xs font-semibold text-[var(--home-text-on-dark)]`} aria-hidden>
      {initials}
    </span>
  );
}

const drawerLinkCls =
  'block rounded-xl px-4 py-3.5 text-base font-semibold tracking-wide text-[var(--home-text-on-dark)]/90 transition-colors active:bg-white/[0.08]';

/**
 * Header profile control: signed-in user, account link, optional portal shortcuts, sign out.
 * Use `forMobileDrawer` inside the hamburger panel so “Logged in” + avatar + actions are not duplicated in the top bar.
 */
export function UserProfileMenu({
  forMobileDrawer = false,
  onNavigate,
}: {
  forMobileDrawer?: boolean;
  /** Close mobile nav after navigation (AppShell). */
  onNavigate?: () => void;
}) {
  const { profile, role, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const closeAll = () => {
    setOpen(false);
    onNavigate?.();
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer, { passive: true });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
    };
  }, [open]);

  if (!profile) return null;

  const name = profileDisplayName(profile);
  const loginEmail = profile.email ?? '—';

  if (forMobileDrawer) {
    return (
      <div className="mt-2 border-t border-white/[0.08] pt-4">
        <p className="px-4 pb-2 font-mono text-[0.6rem] font-medium uppercase tracking-[0.14em] text-[var(--home-muted-on-dark)]">
          Account
        </p>
        <p className="px-4 pb-3 text-sm leading-snug">
          <span className="text-[var(--home-muted-on-dark)]">Logged in: </span>
          <span className="font-medium break-all text-[var(--home-text-on-dark)]">{loginEmail}</span>
        </p>
        <div className="flex items-center gap-3 px-4 pb-2">
          <AvatarThumb profile={profile} />
          <span className="min-w-0 truncate font-medium text-[var(--home-text-on-dark)]">{name}</span>
        </div>
        <Link to="/dashboard/account" className={drawerLinkCls} onClick={closeAll}>
          Account &amp; profile
        </Link>
        {(role === ROLES.INVESTOR || role === ROLES.STAFF || role === ROLES.ADMIN) && (
          <Link to="/dashboard" className={drawerLinkCls} onClick={closeAll}>
            Investor dashboard
          </Link>
        )}
        {role === ROLES.ADMIN ? (
          <Link to="/admin" className={drawerLinkCls} onClick={closeAll}>
            Admin console
          </Link>
        ) : null}
        {(role === ROLES.ADMIN || role === ROLES.STAFF) && (
          <Link to="/admin/crm" className={drawerLinkCls} onClick={closeAll}>
            CRM
          </Link>
        )}
        {role === ROLES.STAFF ? (
          <Link to="/staff" className={drawerLinkCls} onClick={closeAll}>
            Staff queue
          </Link>
        ) : null}
        <button
          type="button"
          className="mt-1 w-full rounded-xl px-4 py-3.5 text-left text-base font-semibold text-[var(--home-muted-on-dark)] transition-colors active:bg-white/[0.08]"
          onClick={() => {
            closeAll();
            void signOut();
          }}
        >
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 max-w-full shrink-0 items-center gap-2 sm:gap-3">
      <p className="min-w-0 max-w-[9.5rem] truncate text-right text-xs sm:max-w-[11rem] sm:text-sm md:max-w-[14rem] lg:max-w-[18rem] xl:max-w-[22rem] 2xl:max-w-[28rem]">
        <span className="text-[var(--home-muted-on-dark)]">Logged in: </span>
        <span className="font-medium text-[var(--home-text-on-dark)]">{loginEmail}</span>
      </p>

      <div ref={wrapRef} className="relative shrink-0">
        <button
          type="button"
          className="rounded-full p-0 transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--home-miami-blue)]/40"
          aria-expanded={open}
          aria-haspopup="menu"
          aria-label="Account menu"
          onClick={() => setOpen((v) => !v)}
        >
          <AvatarThumb profile={profile} />
        </button>

        {open ? (
          <div
            className={`absolute right-0 z-[60] mt-2 min-w-[min(90vw,260px)] py-2 shadow-[0_24px_60px_-20px_rgb(0_0_0/0.75)] ${SITE_GLASS_CARD}`}
            role="menu"
          >
          <div className="border-b border-white/[0.08] px-4 py-3 sm:hidden">
            <p className="truncate text-sm font-medium text-[var(--home-text-on-dark)]">{name}</p>
            {profile.email ? (
              <p className="truncate text-xs text-[var(--home-muted-on-dark)]">{profile.email}</p>
            ) : null}
          </div>

          <Link
            to="/dashboard/account"
            role="menuitem"
            className="block px-4 py-2.5 text-sm text-[var(--home-text-on-dark)] transition hover:bg-white/[0.06]"
            onClick={() => setOpen(false)}
          >
            Account &amp; profile
          </Link>

          {(role === ROLES.INVESTOR || role === ROLES.STAFF || role === ROLES.ADMIN) && (
            <Link
              to="/dashboard"
              role="menuitem"
              className="block px-4 py-2.5 text-sm text-[var(--home-text-on-dark)] transition hover:bg-white/[0.06]"
              onClick={() => setOpen(false)}
            >
              Investor dashboard
            </Link>
          )}

          {role === ROLES.ADMIN ? (
            <Link
              to="/admin"
              role="menuitem"
              className="block px-4 py-2.5 text-sm text-[var(--home-text-on-dark)] transition hover:bg-white/[0.06]"
              onClick={() => setOpen(false)}
            >
              Admin console
            </Link>
          ) : null}

          {(role === ROLES.ADMIN || role === ROLES.STAFF) && (
            <Link
              to="/admin/crm"
              role="menuitem"
              className="block px-4 py-2.5 text-sm text-[var(--home-text-on-dark)] transition hover:bg-white/[0.06]"
              onClick={() => setOpen(false)}
            >
              CRM
            </Link>
          )}

          {role === ROLES.STAFF ? (
            <Link
              to="/staff"
              role="menuitem"
              className="block px-4 py-2.5 text-sm text-[var(--home-text-on-dark)] transition hover:bg-white/[0.06]"
              onClick={() => setOpen(false)}
            >
              Staff queue
            </Link>
          ) : null}

          <div className="my-2 border-t border-white/[0.08]" />

          <button
            type="button"
            role="menuitem"
            className="w-full px-4 py-2.5 text-left text-sm text-[var(--home-muted-on-dark)] transition hover:bg-white/[0.06] hover:text-[var(--home-miami-blue)]"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
          >
            Sign out
          </button>
        </div>
      ) : null}
      </div>
    </div>
  );
}
